import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatError } from "../utils/api";
import { Eye, EyeOff, LogIn } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9e7e0d6e-4f16-4092-b7c7-18a05794ec67/artifacts/w0k6jgr2_WhatsApp%20Image%202026-04-07%20at%2017.12.34.jpeg";
const HERO_URL = "https://customer-assets.emergentagent.com/job_9e7e0d6e-4f16-4092-b7c7-18a05794ec67/artifacts/ayf2kve0_Attached%201%20Kavitha.JPG";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.must_change_password) {
        navigate("/force-reset", { replace: true, state: { token: result.password_reset_token } });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Hero Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src={HERO_URL}
          alt="Children enjoying mid-day meals"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#5C5147]/70 to-[#E8956D]/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-extrabold font-[Manrope] leading-tight mb-4">
            Nourishing the<br />Next Generation
          </h2>
          <p className="text-lg text-white/90 leading-relaxed max-w-sm">
            Providing nutritious mid-day meals and breakfast to government school children across Telangana.
          </p>
        </div>
      </div>

      {/* Login Form Panel */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 bg-[#FAFAF8]">
        <div className="w-full max-w-sm animate-fade-in-up">
          {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <img
              src={LOGO_URL}
              alt="Manna Trust Logo"
              className="h-20 w-20 object-contain mb-4 rounded-xl shadow-sm cursor-pointer"
              onClick={() => navigate("/")}
            />
            <h1 className="text-2xl font-extrabold font-[Manrope] text-[#3A322C]">Manna Trust</h1>
            <p className="text-sm text-[#8A7E73] mt-1">Operations Platform</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8A7E73] mb-2">
                Email Address or Phone Number
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                spellCheck={false}
                data-testid="login-email-input"
                placeholder="email@example.com or 9876543210"
                className="w-full bg-white border border-[#E5E1DB] rounded-lg px-4 py-3 text-[#3A322C] focus:ring-2 focus:ring-[#E8956D]/40 focus:border-[#E8956D] outline-none transition-all placeholder:text-[#C4BAAF]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8A7E73] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  className="w-full bg-white border border-[#E5E1DB] rounded-lg px-4 py-3 pr-12 text-[#3A322C] focus:ring-2 focus:ring-[#E8956D]/40 focus:border-[#E8956D] outline-none transition-all placeholder:text-[#C4BAAF]"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7E73] hover:text-[#5C5147]"
                >
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-lg px-4 py-3 text-sm text-[#C2624E]" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#E8956D] text-[#3A322C] hover:bg-[#D48661] transition-colors rounded-lg px-5 py-3 font-bold shadow-sm active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-[#3A322C]/30 border-t-[#3A322C] rounded-full animate-spin" />
              ) : (
                <><LogIn size={18} /> Sign In</>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-[#8A7E73] mt-8">
            Contact your administrator to get access
          </p>
        </div>
      </div>
    </div>
  );
}
